import React, { useState, useRef, useEffect } from 'react';
import { 
  ChevronDown, Menu, X, Globe, Users, HeartCrack, Brain, Cloud,
  HelpCircle, Search, Sunrise, Sprout, TreePine, Crown,
  Heart, Activity, Home, Briefcase, Building2, Church, TrendingUp, BookOpen,
  Sparkles, Columns3, Zap, Eye, Layers, Compass, Shield, Sun, Moon
} from 'lucide-react';

// Navigation data with icons
const navigationData = [
  {
    name: 'Darkness',
    href: '#darkness',
    columns: [
      {
        title: 'UNDERSTANDING DARKNESS',
        items: [
          { name: 'Land', href: '#land', description: 'Spiritual territories & environments', icon: Globe },
          { name: 'People', href: '#people', description: 'How darkness affects humanity', icon: Users }
        ]
      },
      {
        title: 'MANIFESTATIONS',
        items: [
          { name: 'Spiritual Blindness', href: '#blindness', description: 'Unable to perceive truth', icon: Eye },
          { name: 'Bondage', href: '#bondage', description: 'Chains that hold us back', icon: Layers }
        ]
      },
      {
        title: 'MORE',
        items: [
          { name: 'Overview', href: '#darkness-overview', description: null },
          { name: 'Resources', href: '#darkness-resources', description: null },
          { name: 'Get Help', href: '#get-help', description: null }
        ]
      }
    ]
  },
  {
    name: 'Issues',
    href: '#issues',
    columns: [
      {
        title: 'PHYSICAL & EMOTIONAL',
        items: [
          { name: 'Physical Pain', href: '#physical-pain', description: 'Healing for the body', icon: HeartCrack },
          { name: 'Emotional Trauma', href: '#emotional-trauma', description: 'Restoration for the heart', icon: Brain },
          { name: 'Depression', href: '#depression', description: 'Hope in difficult seasons', icon: Cloud }
        ]
      },
      {
        title: 'SUPPORT',
        items: [
          { name: 'Find Help', href: '#find-help', description: 'Connect with support', icon: Heart },
          { name: 'Resources', href: '#issue-resources', description: 'Tools for healing', icon: BookOpen }
        ]
      },
      {
        title: 'MORE',
        items: [
          { name: 'Overview', href: '#issues-overview', description: null },
          { name: 'Stories of Hope', href: '#hope-stories', description: null }
        ]
      }
    ]
  },
  {
    name: 'I Am',
    href: '#where-i-am',
    columns: [
      {
        title: 'EXPLORING',
        items: [
          { name: 'Just Curious', href: '#curious', description: 'Exploring with an open mind', icon: HelpCircle },
          { name: 'Agnostic', href: '#agnostic', description: 'Seeking answers', icon: Search },
          { name: 'Feeling God Exists', href: '#sensing', description: 'Beginning to believe', icon: Sunrise }
        ]
      },
      {
        title: 'GROWING',
        items: [
          { name: 'New Believer', href: '#new-believer', description: 'Fresh in faith', icon: Sprout },
          { name: 'Seasoned Believer', href: '#seasoned', description: 'Deepening your walk', icon: TreePine },
          { name: 'Minister', href: '#minister', description: 'Called to serve', icon: Crown }
        ]
      },
      {
        title: 'MORE',
        items: [
          { name: 'Take Assessment', href: '#assessment', description: null },
          { name: 'Find Your Path', href: '#find-path', description: null }
        ]
      }
    ]
  },
  {
    name: 'Personal',
    href: '#personal',
    columns: [
      {
        title: 'SPIRIT, SOUL & BODY',
        items: [
          { name: 'Spirit', href: '#spiritual', description: 'Your spiritual connection', icon: Sparkles },
          { name: 'Soul', href: '#soul', description: 'Mind, will, and emotions', icon: Brain },
          { name: 'Body', href: '#body', description: 'Physical well-being', icon: Activity }
        ]
      },
      {
        title: 'RELATIONSHIPS',
        items: [
          { name: 'Family', href: '#family', description: 'Strengthening bonds', icon: Home },
          { name: 'Resources', href: '#personal-resources', description: 'Tools for personal growth', icon: BookOpen }
        ]
      },
      {
        title: 'MORE',
        items: [
          { name: 'The 3Strand Cord', href: '#3strand', description: null },
          { name: 'Self Assessment', href: '#self-assessment', description: null }
        ]
      }
    ]
  },
  {
    name: 'Spheres',
    href: '#spheres',
    columns: [
      {
        title: 'PROFESSIONAL LIFE',
        items: [
          { name: 'Work', href: '#work', description: 'Faith in the workplace', icon: Briefcase },
          { name: 'Business', href: '#business', description: 'Kingdom business principles', icon: Building2 }
        ]
      },
      {
        title: 'KINGDOM INFLUENCE',
        items: [
          { name: 'Ministry', href: '#ministry', description: 'Serving and leading', icon: Church },
          { name: 'Investments', href: '#investments', description: 'Stewardship and resources', icon: TrendingUp },
          { name: 'Resources', href: '#sphere-resources', description: 'Tools and guides', icon: BookOpen }
        ]
      },
      {
        title: 'MORE',
        items: [
          { name: 'Overview', href: '#spheres-overview', description: null },
          { name: 'Case Studies', href: '#case-studies', description: null }
        ]
      }
    ]
  },
  {
    name: 'Topics',
    href: '#topics',
    columns: [
      {
        title: 'SPIRITUAL PRACTICES',
        items: [
          { name: 'Meditation', href: '#meditation', description: 'Stillness and contemplation', icon: Sun },
          { name: 'Prayer', href: '#prayer', description: 'Divine communication', icon: Heart }
        ]
      },
      {
        title: 'SPIRITUAL KNOWLEDGE',
        items: [
          { name: 'Angels', href: '#angels', description: 'Heavenly messengers', icon: Shield },
          { name: '7 Pillars of Wisdom', href: '#wisdom', description: 'Foundations of understanding', icon: Columns3 },
          { name: 'Power', href: '#power', description: 'Spiritual authority', icon: Zap }
        ]
      },
      {
        title: 'MORE',
        items: [
          { name: 'All Topics', href: '#all-topics', description: null },
          { name: 'Study Guides', href: '#study-guides', description: null },
          { name: 'Video Library', href: '#video-library', description: null }
        ]
      }
    ]
  }
];

const Header = () => {
  const [activeMenu, setActiveMenu] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMouseEnter = (index) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveMenu(index);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveMenu(null);
    }, 150);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2">
            <div className="relative">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center">
                <span className="text-white font-bold text-sm">3</span>
              </div>
            </div>
            <span className={`font-bold text-xl tracking-tight ${
              scrolled ? 'text-slate-900' : 'text-white'
            }`}>
              3strand Cord
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigationData.map((item, index) => (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                    scrolled
                      ? 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                  } ${activeMenu === index ? (scrolled ? 'bg-slate-100' : 'bg-white/10') : ''}`}
                >
                  {item.name}
                  <ChevronDown className={`w-4 h-4 transition-transform ${
                    activeMenu === index ? 'rotate-180' : ''
                  }`} />
                </button>

                {/* Mega Menu Dropdown */}
                {activeMenu === index && (
                  <div 
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
                    style={{ minWidth: '600px' }}
                    onMouseEnter={() => handleMouseEnter(index)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div className="p-6">
                      <div className="flex gap-8">
                        {item.columns.map((column, colIndex) => (
                          <div key={colIndex} className="flex-1 min-w-[160px]">
                            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                              {column.title}
                            </h4>
                            <div className="space-y-1">
                              {column.items.map((subitem) => (
                                <a
                                  key={subitem.name}
                                  href={subitem.href}
                                  className="flex items-start gap-3 px-2 py-2 rounded-xl hover:bg-slate-50 transition-colors group"
                                >
                                  {subitem.icon && (
                                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center flex-shrink-0 group-hover:from-indigo-100 group-hover:to-purple-100 transition-colors">
                                      <subitem.icon className="w-5 h-5 text-indigo-600" />
                                    </div>
                                  )}
                                  <div>
                                    <span className={`font-medium text-slate-900 group-hover:text-indigo-600 transition-colors ${!subitem.icon ? 'text-sm' : ''}`}>
                                      {subitem.name}
                                    </span>
                                    {subitem.description && (
                                      <p className="text-xs text-slate-500 mt-0.5">
                                        {subitem.description}
                                      </p>
                                    )}
                                  </div>
                                </a>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right side - Sign in and CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="#signin"
              className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                scrolled ? 'text-slate-700 hover:text-slate-900' : 'text-white/90 hover:text-white'
              }`}
            >
              Sign in
              <ChevronDown className="w-4 h-4" />
            </a>
            <a
              href="#journey"
              className="px-5 py-2.5 text-sm font-semibold rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg hover:shadow-indigo-500/25 transition-all flex items-center gap-2"
            >
              Discover Your Journey
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              scrolled ? 'text-slate-700 hover:bg-slate-100' : 'text-white hover:bg-white/10'
            }`}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden animate-in fade-in slide-in-from-top-2">
            <div className="p-4 max-h-[70vh] overflow-y-auto">
              {navigationData.map((item) => (
                <div key={item.name} className="mb-4">
                  <h3 className="font-semibold text-slate-900 px-3 py-2">{item.name}</h3>
                  <div className="space-y-1">
                    {item.columns.flatMap(col => col.items).map((subitem) => (
                      <a
                        key={subitem.name}
                        href={subitem.href}
                        className="flex items-center gap-3 px-3 py-2 text-sm text-slate-600 hover:text-indigo-600 hover:bg-slate-50 rounded-lg transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {subitem.icon && <subitem.icon className="w-4 h-4" />}
                        {subitem.name}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
              <div className="border-t border-slate-100 pt-4 mt-4 space-y-2">
                <a href="#signin" className="block px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg">
                  Sign in
                </a>
                <a href="#journey" className="block px-3 py-3 text-sm font-semibold text-center text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl">
                  Discover Your Journey
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
